/**
 * GenViz / SmartFence — Énumérés du contrat partagé.
 *
 * Règle générale (docs/06-revue-critique.md) : l'API ne transporte AUCUNE chaîne d'affichage.
 * Tout ce qui est catégoriel passe par les énumérés ci-dessous ; la traduction FR/EN/ES et le
 * formatage sont à la charge de l'application (i18next côté mobile, i18n côté back-office).
 *
 * Convention : chaque énuméré est un objet `as const` doublé d'un type du même nom.
 * On évite `enum` TypeScript (non effaçable, incompatible `isolatedModules` + Babel/Metro).
 * La valeur sérialisée sur le fil est TOUJOURS la valeur littérale de l'objet.
 */

/* -------------------------------------------------------------------------- */
/* Identité, rôles et comptes                                                  */
/* -------------------------------------------------------------------------- */

/** Rôle d'un utilisateur AU SEIN d'une exploitation (table Utilisateur.role). */
export const RoleUtilisateur = {
  /** Administrateur de l'exploitation. Exactement un par exploitation. */
  ADMIN: 'ADMIN',
  /** Utilisateur simple, rattaché par invitation. */
  USER: 'USER',
} as const;
export type RoleUtilisateur = (typeof RoleUtilisateur)[keyof typeof RoleUtilisateur];

/**
 * Rôle d'un opérateur interne GenViz (back-office). N'est JAMAIS attribuable depuis le mobile
 * et n'est jamais porté par Utilisateur.role (compte séparé). Décision D-09.
 */
export const RoleInterne = {
  /** Support / SAV : diagnostic, désassociation d'appareil. */
  STAFF_SUPPORT: 'STAFF_SUPPORT',
  /** Opérateur usine : import de stock, appairage module ↔ électrificateur. */
  STAFF_OPERATEUR: 'STAFF_OPERATEUR',
  /** Administrateur du back-office : tout le périmètre interne. */
  STAFF_ADMIN: 'STAFF_ADMIN',
} as const;
export type RoleInterne = (typeof RoleInterne)[keyof typeof RoleInterne];

/** Statut du compte utilisateur (Utilisateur.statut). */
export const StatutUtilisateur = {
  ACTIF: 'ACTIF',
  /** Compte désactivé : connexion refusée (erreur COMPTE_DESACTIVE). */
  DESACTIVE: 'DESACTIVE',
  /** Sorti de l'exploitation par l'administrateur (écran 05B.2). */
  RETIRE: 'RETIRE',
} as const;
export type StatutUtilisateur = (typeof StatutUtilisateur)[keyof typeof StatutUtilisateur];

/** Statut d'une ligne de l'écran 05B (utilisateur confirmé OU invitation en attente). */
export const StatutMembre = {
  ACTIF: 'ACTIF',
  INVITATION_EN_ATTENTE: 'INVITATION_EN_ATTENTE',
  DESACTIVE: 'DESACTIVE',
  RETIRE: 'RETIRE',
} as const;
export type StatutMembre = (typeof StatutMembre)[keyof typeof StatutMembre];

/** Statut d'une exploitation. */
export const StatutExploitation = {
  ACTIVE: 'ACTIVE',
  SUSPENDUE: 'SUSPENDUE',
  SUPPRIMEE: 'SUPPRIMEE',
} as const;
export type StatutExploitation = (typeof StatutExploitation)[keyof typeof StatutExploitation];

/** Statut d'une invitation (écrans 01D, 05B, 05B.1). */
export const StatutInvitation = {
  EN_ATTENTE: 'EN_ATTENTE',
  ACCEPTEE: 'ACCEPTEE',
  EXPIREE: 'EXPIREE',
  REVOQUEE: 'REVOQUEE',
} as const;
export type StatutInvitation = (typeof StatutInvitation)[keyof typeof StatutInvitation];

/** Statut d'une demande de transfert d'administrateur (écran 05B.3). */
export const StatutDemandeTransfert = {
  EN_ATTENTE: 'EN_ATTENTE',
  ACCEPTEE: 'ACCEPTEE',
  REFUSEE: 'REFUSEE',
  EXPIREE: 'EXPIREE',
  ANNULEE: 'ANNULEE',
} as const;
export type StatutDemandeTransfert =
  (typeof StatutDemandeTransfert)[keyof typeof StatutDemandeTransfert];

/* -------------------------------------------------------------------------- */
/* Appareils                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * État métier d'un électrificateur — pilote la couleur du marqueur carte (02A) et
 * du marker de liste (04A).
 *
 * VERT = NOMINAL, ORANGE = ATTENTION, ROUGE = CRITIQUE, NOIR = ETEINT (volontairement hors tension).
 */
export const EtatAppareil = {
  NOMINAL: 'NOMINAL',
  ATTENTION: 'ATTENTION',
  CRITIQUE: 'CRITIQUE',
  /** Éteint volontairement par commande utilisateur (marqueur BLEU depuis FR_00053). */
  ETEINT: 'ETEINT',
} as const;
export type EtatAppareil = (typeof EtatAppareil)[keyof typeof EtatAppareil];

/**
 * Couleur normalisée d'un état ou d'une criticité. Jeton de design, pas une chaîne d'affichage.
 *
 * CHARTE DES MARQUEURS DE CARTE, fixée par le retour client FR_00053 du 23 septembre 2026 :
 *   VERT    fonctionnement nominal
 *   ORANGE  message d'attention
 *   ROUGE   alerte
 *   BLEU    éteint VOLONTAIREMENT par l'utilisateur
 *   GRIS    plus de connectivité
 *
 * `NOIR` reste en usage, mais UNIQUEMENT pour les évènements du flux d'alertes (écran 03A, les
 * « lignes noires » de la spécification). Il ne colore plus aucun marqueur de carte : l'éteint
 * est passé au BLEU, et le NOIR que la première rédaction de FR_00053 réservait à « plus de
 * connectivité depuis une semaine » a été abandonné — un seul état gris, sans seuil long.
 */
export const Couleur = {
  VERT: 'VERT',
  ORANGE: 'ORANGE',
  ROUGE: 'ROUGE',
  BLEU: 'BLEU',
  GRIS: 'GRIS',
  /** Évènements du flux d'alertes seulement. Plus aucun marqueur de carte. */
  NOIR: 'NOIR',
} as const;
export type Couleur = (typeof Couleur)[keyof typeof Couleur];

/** Alimentation de l'électrificateur, telle que remontée / commandée. */
export const EtatAlimentation = {
  ON: 'ON',
  OFF: 'OFF',
  INCONNU: 'INCONNU',
} as const;
export type EtatAlimentation = (typeof EtatAlimentation)[keyof typeof EtatAlimentation];

/** Connectivité dérivée du dernier message reçu (FrequenceHeartBeat × NombreHeartBeatNonRecus). */
export const EtatConnectivite = {
  EN_LIGNE: 'EN_LIGNE',
  HORS_LIGNE: 'HORS_LIGNE',
} as const;
export type EtatConnectivite = (typeof EtatConnectivite)[keyof typeof EtatConnectivite];

/** Modèle d'électrificateur — conditionne l'applicabilité des alertes batterie. */
export const ModeleAppareil = {
  PORTABLE_SOLAIRE: 'PORTABLE_SOLAIRE',
  SECTEUR: 'SECTEUR',
  BATTERIE_12V: 'BATTERIE_12V',
} as const;
export type ModeleAppareil = (typeof ModeleAppareil)[keyof typeof ModeleAppareil];

/** Cycle de vie d'un appareil dans le stock backend (back-office). */
export const StatutStockAppareil = {
  EN_STOCK: 'EN_STOCK',
  ENREGISTRE: 'ENREGISTRE',
  RETIRE: 'RETIRE',
  SAV: 'SAV',
  HORS_SERVICE: 'HORS_SERVICE',
} as const;
export type StatutStockAppareil =
  (typeof StatutStockAppareil)[keyof typeof StatutStockAppareil];

/** Origine de la saisie d'un numéro de série (écrans 01A / 04A.4). */
export const OrigineNumeroSerie = {
  QR: 'QR',
  MANUEL: 'MANUEL',
} as const;
export type OrigineNumeroSerie =
  (typeof OrigineNumeroSerie)[keyof typeof OrigineNumeroSerie];

/** Tri de la liste des électrificateurs (04A). */
export const TriListeAppareils = {
  NOM: 'NOM',
  STATUT: 'STATUT',
} as const;
export type TriListeAppareils = (typeof TriListeAppareils)[keyof typeof TriListeAppareils];

/** Synchronisation du paramétrage souhaité (backend) avec le paramétrage appliqué (objet). */
export const StatutSynchronisation = {
  SYNCHRONISE: 'SYNCHRONISE',
  EN_ATTENTE: 'EN_ATTENTE',
  DIVERGENT: 'DIVERGENT',
} as const;
export type StatutSynchronisation =
  (typeof StatutSynchronisation)[keyof typeof StatutSynchronisation];

/* -------------------------------------------------------------------------- */
/* Alertes et évènements                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Liste COMPLÈTE du tableau des alertes de la spec (§5.1, l.973-1007) — 13 valeurs.
 * Alertes et évènements partagent une seule entité et un seul flux (03A).
 */
export const TypeAlerte = {
  /** Fonctionnement nominal (ligne verte du tableau). */
  NOMINAL: 'NOMINAL',
  /** Tension d'impulsion temporairement basse (quelques impulsions sous le seuil). */
  TENSION_TEMPORAIREMENT_BASSE: 'TENSION_TEMPORAIREMENT_BASSE',
  /** Batterie de l'électrificateur entre 50 % et 20 % (modèles PORTABLE_SOLAIRE). */
  BATTERIE_ENTRE_50_20: 'BATTERIE_ENTRE_50_20',
  /** Absence temporaire de communication (heartbeats manqués, seuil ORANGE). */
  ABSENCE_TEMPORAIRE_COMMUNICATION: 'ABSENCE_TEMPORAIRE_COMMUNICATION',
  /** Tension d'impulsion trop basse depuis plusieurs cycles (seuil ROUGE). */
  TENSION_TROP_BASSE: 'TENSION_TROP_BASSE',
  /** Électrificateur en mouvement (anti-vol) — levée si SurveillanceMouvement = true. */
  ELECTRIFICATEUR_EN_MOUVEMENT: 'ELECTRIFICATEUR_EN_MOUVEMENT',
  /** Batterie de l'électrificateur sous 20 %. */
  BATTERIE_SOUS_20: 'BATTERIE_SOUS_20',
  /** Batterie de secours du module de connectivité sous son seuil. */
  BATTERIE_SECOURS_SOUS_SEUIL: 'BATTERIE_SECOURS_SOUS_SEUIL',
  /** Perte de communication (seuil ROUGE). */
  PERTE_COMMUNICATION: 'PERTE_COMMUNICATION',
  /** Évènement : électrificateur éteint (commande utilisateur). */
  ELECTRIFICATEUR_ETEINT: 'ELECTRIFICATEUR_ETEINT',
  /** Évènement : électrificateur mis en route. */
  ELECTRIFICATEUR_MIS_EN_ROUTE: 'ELECTRIFICATEUR_MIS_EN_ROUTE',
  /** Évènement : arrêt du mouvement, corrélé à l'alerte de mouvement d'origine. */
  ARRET_MOUVEMENT: 'ARRET_MOUVEMENT',
  /** Évènement : retour au fonctionnement normal, corrélé à l'alerte critique d'origine. */
  RETOUR_FONCTIONNEMENT_NORMAL: 'RETOUR_FONCTIONNEMENT_NORMAL',
  /**
   * Évènement : la tension de gardiennage a été modifiée (FR_00072, 28/09/2026). Placé dans le
   * fil AVANT l'avertissement ou le retour à la normale qu'il provoque, il en donne la cause.
   */
  SEUIL_GARDIENNAGE_MODIFIE: 'SEUIL_GARDIENNAGE_MODIFIE',
} as const;
export type TypeAlerte = (typeof TypeAlerte)[keyof typeof TypeAlerte];

/** Nature : sépare les deux moitiés de l'écran 03A. Dérivée du type. */
export const NatureAlerte = {
  ALERTE: 'ALERTE',
  EVENEMENT: 'EVENEMENT',
} as const;
export type NatureAlerte = (typeof NatureAlerte)[keyof typeof NatureAlerte];

/** Criticité. La partie haute de 03A ne liste que CRITIQUE. Dérivée du type. */
export const CriticiteAlerte = {
  NOMINAL: 'NOMINAL',
  ATTENTION: 'ATTENTION',
  CRITIQUE: 'CRITIQUE',
  EVENEMENT: 'EVENEMENT',
} as const;
export type CriticiteAlerte = (typeof CriticiteAlerte)[keyof typeof CriticiteAlerte];

/** Statut d'une alerte. Les évènements sont créés directement RESOLUE (ponctuels). */
export const StatutAlerte = {
  ACTIVE: 'ACTIVE',
  RESOLUE: 'RESOLUE',
} as const;
export type StatutAlerte = (typeof StatutAlerte)[keyof typeof StatutAlerte];

/** Portée demandée à `GET /v1/alerts` : partie haute (critiques) ou liste complète. */
export const PorteeListeAlertes = {
  CRITIQUES: 'CRITIQUES',
  /**
   * Partie haute de 03A : les alertes critiques EN COURS (lues ou non) et les critiques non lues.
   * Retour du 25/09 : une alerte critique toujours active, mais déjà ouverte, disparaissait du
   * haut de l'écran et ne restait visible que dans l'historique.
   */
  A_TRAITER: 'A_TRAITER',
  TOUT: 'TOUT',
} as const;
export type PorteeListeAlertes =
  (typeof PorteeListeAlertes)[keyof typeof PorteeListeAlertes];

/** Portée d'une purge d'historique (écran 03A.1). */
export const PorteePurge = {
  TOUT: 'TOUT',
  LUES_UNIQUEMENT: 'LUES_UNIQUEMENT',
} as const;
export type PorteePurge = (typeof PorteePurge)[keyof typeof PorteePurge];

/** Correspondance type d'alerte → nature. Table de vérité unique pour les trois applications. */
export const NATURE_PAR_TYPE_ALERTE: Readonly<Record<TypeAlerte, NatureAlerte>> = {
  NOMINAL: NatureAlerte.EVENEMENT,
  TENSION_TEMPORAIREMENT_BASSE: NatureAlerte.ALERTE,
  BATTERIE_ENTRE_50_20: NatureAlerte.ALERTE,
  ABSENCE_TEMPORAIRE_COMMUNICATION: NatureAlerte.ALERTE,
  TENSION_TROP_BASSE: NatureAlerte.ALERTE,
  ELECTRIFICATEUR_EN_MOUVEMENT: NatureAlerte.ALERTE,
  BATTERIE_SOUS_20: NatureAlerte.ALERTE,
  BATTERIE_SECOURS_SOUS_SEUIL: NatureAlerte.ALERTE,
  PERTE_COMMUNICATION: NatureAlerte.ALERTE,
  ELECTRIFICATEUR_ETEINT: NatureAlerte.EVENEMENT,
  ELECTRIFICATEUR_MIS_EN_ROUTE: NatureAlerte.EVENEMENT,
  ARRET_MOUVEMENT: NatureAlerte.EVENEMENT,
  RETOUR_FONCTIONNEMENT_NORMAL: NatureAlerte.EVENEMENT,
  SEUIL_GARDIENNAGE_MODIFIE: NatureAlerte.EVENEMENT,
} as const;

/** Correspondance type d'alerte → criticité (docs/01, entité AlerteEvenement). */
export const CRITICITE_PAR_TYPE_ALERTE: Readonly<Record<TypeAlerte, CriticiteAlerte>> = {
  NOMINAL: CriticiteAlerte.NOMINAL,
  TENSION_TEMPORAIREMENT_BASSE: CriticiteAlerte.ATTENTION,
  BATTERIE_ENTRE_50_20: CriticiteAlerte.ATTENTION,
  ABSENCE_TEMPORAIRE_COMMUNICATION: CriticiteAlerte.ATTENTION,
  TENSION_TROP_BASSE: CriticiteAlerte.CRITIQUE,
  ELECTRIFICATEUR_EN_MOUVEMENT: CriticiteAlerte.CRITIQUE,
  BATTERIE_SOUS_20: CriticiteAlerte.CRITIQUE,
  BATTERIE_SECOURS_SOUS_SEUIL: CriticiteAlerte.CRITIQUE,
  PERTE_COMMUNICATION: CriticiteAlerte.CRITIQUE,
  ELECTRIFICATEUR_ETEINT: CriticiteAlerte.EVENEMENT,
  ELECTRIFICATEUR_MIS_EN_ROUTE: CriticiteAlerte.EVENEMENT,
  ARRET_MOUVEMENT: CriticiteAlerte.EVENEMENT,
  RETOUR_FONCTIONNEMENT_NORMAL: CriticiteAlerte.EVENEMENT,
  SEUIL_GARDIENNAGE_MODIFIE: CriticiteAlerte.EVENEMENT,
} as const;

/** Correspondance criticité → couleur. */
export const COULEUR_PAR_CRITICITE: Readonly<Record<CriticiteAlerte, Couleur>> = {
  NOMINAL: Couleur.VERT,
  ATTENTION: Couleur.ORANGE,
  CRITIQUE: Couleur.ROUGE,
  EVENEMENT: Couleur.NOIR,
} as const;

/**
 * Correspondance type d'alerte → couleur D'AFFICHAGE.
 *
 * POURQUOI CETTE TABLE EXISTE À CÔTÉ DE `COULEUR_PAR_CRITICITE`. Les quatre ÉVÈNEMENTS partagent
 * la même criticité (`EVENEMENT`) et se retrouvaient donc tous en NOIR, y compris le retour à la
 * normale. Retour client FR_00007 : « afficher un Retour à la normale en VERT et ne garder le
 * GRIS que pour la mise hors tension de l'électrificateur volontairement, c'est-à-dire par une
 * commande de l'utilisateur ».
 *
 * La criticité, elle, ne change pas : elle sert au filtrage des notifications et au tri du flux
 * 03A, où un retour à la normale n'est toujours pas une alerte. Seule la COULEUR se raffine.
 *
 * Le gris reste donc réservé à ce qui dit « l'appareil ne protège plus, et c'est voulu ».
 */
export const COULEUR_PAR_TYPE_ALERTE: Readonly<Record<TypeAlerte, Couleur>> = {
  NOMINAL: Couleur.VERT,
  TENSION_TEMPORAIREMENT_BASSE: Couleur.ORANGE,
  BATTERIE_ENTRE_50_20: Couleur.ORANGE,
  ABSENCE_TEMPORAIRE_COMMUNICATION: Couleur.ORANGE,
  TENSION_TROP_BASSE: Couleur.ROUGE,
  ELECTRIFICATEUR_EN_MOUVEMENT: Couleur.ROUGE,
  BATTERIE_SOUS_20: Couleur.ROUGE,
  BATTERIE_SECOURS_SOUS_SEUIL: Couleur.ROUGE,
  PERTE_COMMUNICATION: Couleur.ROUGE,
  // Les seuls gris : l'électrificateur est hors tension sur commande de l'utilisateur.
  ELECTRIFICATEUR_ETEINT: Couleur.NOIR,
  // Remise en route, arrêt du mouvement, retour à la normale : la situation redevient saine.
  ELECTRIFICATEUR_MIS_EN_ROUTE: Couleur.VERT,
  ARRET_MOUVEMENT: Couleur.VERT,
  RETOUR_FONCTIONNEMENT_NORMAL: Couleur.VERT,
  // Réglage modifié par l'utilisateur : information neutre, ni saine ni anormale.
  SEUIL_GARDIENNAGE_MODIFIE: Couleur.NOIR,
} as const;

/** Correspondance état d'appareil → couleur de marqueur (02A / 04A). */
export const COULEUR_PAR_ETAT_APPAREIL: Readonly<Record<EtatAppareil, Couleur>> = {
  NOMINAL: Couleur.VERT,
  ATTENTION: Couleur.ORANGE,
  CRITIQUE: Couleur.ROUGE,
  /** BLEU depuis FR_00053. Était NOIR, qui ne sert plus qu'aux évènements. */
  ETEINT: Couleur.BLEU,
} as const;

/**
 * Couleur du marqueur d'un appareil, connectivité COMPRISE.
 *
 * LA PERTE DE CONNECTIVITÉ PRIME SUR TOUT LE RESTE. Un appareil dont on n'a plus de nouvelles
 * ne peut pas être affiché vert, orange ou rouge : ces trois couleurs affirment quelque chose
 * sur l'état de la clôture, et on ne sait plus rien. Le gris dit « pas de nouvelles », ce qui
 * est la seule chose vraie.
 *
 * Elle prime aussi sur le BLEU, et c'est le point le plus discutable de cette fonction : un
 * appareil éteint volontairement puis passé hors couverture s'affiche gris, pas bleu. Le bleu
 * affirmerait « éteint, comme vous l'avez demandé » alors que la dernière nouvelle date d'il y
 * a des heures — on ne sait pas s'il est encore éteint, ni même s'il est encore là. À inverser
 * si le terrain préfère la mémoire de l'intention à l'honnêteté de l'ignorance.
 *
 * UNE SEULE définition, partagée par le backend, l'application et le back-office. Trois
 * dérivations séparées divergeraient à la première évolution de la charte — c'est déjà arrivé.
 */
export function couleurMarqueur(etat: EtatAppareil, connectivite: EtatConnectivite): Couleur {
  if (connectivite === EtatConnectivite.HORS_LIGNE) return Couleur.GRIS;
  return COULEUR_PAR_ETAT_APPAREIL[etat];
}

/* -------------------------------------------------------------------------- */
/* Commandes descendantes                                                      */
/* -------------------------------------------------------------------------- */

/** Types de commande descendante (entité Commande). */
export const TypeCommande = {
  /** Mise sous tension (écran 04B). */
  POWER_ON: 'POWER_ON',
  /** Mise hors tension (écran 04B) — génère un évènement ELECTRIFICATEUR_ETEINT. */
  POWER_OFF: 'POWER_OFF',
  /** Session de mesure temps réel « Relancer le suivi » (écran 04B). */
  START_MEASURE_SESSION: 'START_MEASURE_SESSION',
  /** Écriture de la tension de gardiennage (écran 04C). */
  SET_GUARD_VOLTAGE: 'SET_GUARD_VOLTAGE',
  /** Écriture de la fréquence de remontée GPS en mouvement (écran 04C). */
  SET_GEOLOC_FREQ: 'SET_GEOLOC_FREQ',
  /** Activation / désactivation de la surveillance de mouvement (écran 04C). */
  SET_MOVEMENT_WATCH: 'SET_MOVEMENT_WATCH',
  /** Écriture de la cadence de heartbeat (back-office). */
  SET_HEARTBEAT_PERIOD: 'SET_HEARTBEAT_PERIOD',
  /** Redémarrage du module (back-office / SAV). */
  REBOOT: 'REBOOT',
  /** Mise à jour firmware (back-office, hors V1). */
  OTA_UPDATE: 'OTA_UPDATE',
  /** Désassociation forcée (back-office / SAV). */
  UNBIND: 'UNBIND',
} as const;
export type TypeCommande = (typeof TypeCommande)[keyof typeof TypeCommande];

/** Types de commande déclenchables depuis l'application mobile (écran 04B). */
export const TYPES_COMMANDE_MOBILE = [
  TypeCommande.POWER_ON,
  TypeCommande.POWER_OFF,
  TypeCommande.START_MEASURE_SESSION,
] as const;
export type TypeCommandeMobile = (typeof TYPES_COMMANDE_MOBILE)[number];

/**
 * Cycle de vie d'une commande. ACQUITTEE = reçue par l'objet ;
 * APPLIQUEE = état réel confirmé par une remontée d'état (jamais sur l'ACK protocolaire seul).
 */
export const StatutCommande = {
  EN_ATTENTE: 'EN_ATTENTE',
  ENVOYEE: 'ENVOYEE',
  ACQUITTEE: 'ACQUITTEE',
  APPLIQUEE: 'APPLIQUEE',
  ECHOUEE: 'ECHOUEE',
  EXPIREE: 'EXPIREE',
  ANNULEE: 'ANNULEE',
} as const;
export type StatutCommande = (typeof StatutCommande)[keyof typeof StatutCommande];

/** Statuts terminaux : plus aucune transition possible. */
export const STATUTS_COMMANDE_TERMINAUX = [
  StatutCommande.APPLIQUEE,
  StatutCommande.ECHOUEE,
  StatutCommande.EXPIREE,
  StatutCommande.ANNULEE,
] as const;

/** Cause d'échec d'une commande (Commande.code_erreur). */
export const CauseEchecCommande = {
  APPAREIL_HORS_LIGNE: 'APPAREIL_HORS_LIGNE',
  REFUS_APPAREIL: 'REFUS_APPAREIL',
  DELAI_DEPASSE: 'DELAI_DEPASSE',
  NON_SUPPORTEE: 'NON_SUPPORTEE',
} as const;
export type CauseEchecCommande =
  (typeof CauseEchecCommande)[keyof typeof CauseEchecCommande];

/** Acquittement renvoyé par l'objet sur une commande descendante. */
export const EtatAcquittement = {
  /** Commande reçue par l'objet. */
  ACK: 'ACK',
  /** Commande réellement appliquée, état confirmé. */
  APPLIED: 'APPLIED',
  /** Commande refusée par l'objet. */
  NACK: 'NACK',
} as const;
export type EtatAcquittement = (typeof EtatAcquittement)[keyof typeof EtatAcquittement];

/* -------------------------------------------------------------------------- */
/* Télémétrie, positions, historique                                           */
/* -------------------------------------------------------------------------- */

/** Fenêtres de l'historique de tension (écran 04E). Défaut applicatif : H24. */
export const PeriodeHistorique = {
  /** 2 heures. */
  H2: '2h',
  /** 24 heures (défaut retenu, la spec n'en désigne aucun). */
  H24: '24h',
  /** 1 semaine. */
  J7: '7j',
} as const;
export type PeriodeHistorique = (typeof PeriodeHistorique)[keyof typeof PeriodeHistorique];

/** Granularité d'agrégation servie pour une fenêtre donnée. */
export const GranulariteAgregat = {
  BRUT: 'BRUT',
  MIN_5: '5min',
  HEURE: '1h',
} as const;
export type GranulariteAgregat =
  (typeof GranulariteAgregat)[keyof typeof GranulariteAgregat];

/** Origine d'une mesure de tension. */
export const SourceMesure = {
  /** Remontée de fond, adossée au heartbeat. */
  PERIODIQUE: 'PERIODIQUE',
  /** Session ouverte par « Relancer le suivi » (écran 04B). */
  SESSION_TEMPS_REEL: 'SESSION_TEMPS_REEL',
} as const;
export type SourceMesure = (typeof SourceMesure)[keyof typeof SourceMesure];

/** Mode demandé à `GET /v1/devices/{id}/positions` (écrans 04D / 03B). */
export const ModePositionsDemande = {
  /** Parcours si une alerte de mouvement est encore historisée, sinon dernier point. */
  AUTO: 'AUTO',
  POSITION: 'POSITION',
  PARCOURS: 'PARCOURS',
  /** Toute la vie de l'appareil, un point par heure (écran 04D, retour du 25/09/2026). */
  HISTORIQUE: 'HISTORIQUE',
} as const;
export type ModePositionsDemande =
  (typeof ModePositionsDemande)[keyof typeof ModePositionsDemande];

/** Mode effectivement renvoyé. */
export const ModePositions = {
  POSITION: 'POSITION',
  PARCOURS: 'PARCOURS',
  HISTORIQUE: 'HISTORIQUE',
} as const;
export type ModePositions = (typeof ModePositions)[keyof typeof ModePositions];

/** Statut d'un épisode de mouvement. */
export const StatutSessionMouvement = {
  EN_COURS: 'EN_COURS',
  TERMINEE: 'TERMINEE',
} as const;
export type StatutSessionMouvement =
  (typeof StatutSessionMouvement)[keyof typeof StatutSessionMouvement];

/* -------------------------------------------------------------------------- */
/* Préférences, notifications, contenus                                        */
/* -------------------------------------------------------------------------- */

/** Langues de l'application (spec §3.3). */
export const Langue = {
  FR: 'fr',
  EN: 'en',
  ES: 'es',
} as const;
export type Langue = (typeof Langue)[keyof typeof Langue];

/** Langue par défaut. */
export const LANGUE_PAR_DEFAUT: Langue = Langue.FR;

/** Canal de notification. Le SMS est déclaré mais non fonctionnel (spec l.2378). */
export const CanalNotification = {
  PUSH: 'PUSH',
  EMAIL: 'EMAIL',
  SMS: 'SMS',
} as const;
export type CanalNotification =
  (typeof CanalNotification)[keyof typeof CanalNotification];

/** Filtre de criticité minimale pour les notifications (écran 05C). */
export const CriticiteMinimaleNotification = {
  TOUT: 'TOUT',
  ATTENTION_ET_PLUS: 'ATTENTION_ET_PLUS',
  CRITIQUE_UNIQUEMENT: 'CRITIQUE_UNIQUEMENT',
} as const;
export type CriticiteMinimaleNotification =
  (typeof CriticiteMinimaleNotification)[keyof typeof CriticiteMinimaleNotification];

/** Statut d'une notification sortante (back-office / diagnostic). */
export const StatutNotification = {
  EN_ATTENTE: 'EN_ATTENTE',
  ENVOYEE: 'ENVOYEE',
  ECHOUEE: 'ECHOUEE',
  BOUNCE: 'BOUNCE',
} as const;
export type StatutNotification =
  (typeof StatutNotification)[keyof typeof StatutNotification];

/** Plateforme d'un jeton push. Le livrable est un APK Android. */
export const PlateformePush = {
  ANDROID: 'ANDROID',
} as const;
export type PlateformePush = (typeof PlateformePush)[keyof typeof PlateformePush];

/** Style de carte (préférence locale de l'app, jamais persistée côté serveur). */
export const StyleCarte = {
  PLAN: 'PLAN',
  SATELLITE: 'SATELLITE',
} as const;
export type StyleCarte = (typeof StyleCarte)[keyof typeof StyleCarte];

/** Type de ressource documentaire (écran 00B). */
export const TypeDocumentation = {
  PDF: 'PDF',
  VIDEO: 'VIDEO',
  HTML: 'HTML',
} as const;
export type TypeDocumentation =
  (typeof TypeDocumentation)[keyof typeof TypeDocumentation];

/** Statut d'un ticket de support (hors V1 selon la revue critique). */
export const StatutTicketSupport = {
  OUVERT: 'OUVERT',
  EN_COURS: 'EN_COURS',
  RESOLU: 'RESOLU',
  FERME: 'FERME',
} as const;
export type StatutTicketSupport =
  (typeof StatutTicketSupport)[keyof typeof StatutTicketSupport];

/* -------------------------------------------------------------------------- */
/* Paramètres, ingestion, audit                                                */
/* -------------------------------------------------------------------------- */

/** Où réside physiquement un paramètre usine. */
export const PorteeParametre = {
  /** Flash du module de connectivité. */
  OBJET_FLASH: 'OBJET_FLASH',
  /** Exploité par le backend (détection d'alertes, seuils de communication). */
  BACKEND: 'BACKEND',
  /** Constante d'affichage servie à l'application. */
  APP: 'APP',
} as const;
export type PorteeParametre = (typeof PorteeParametre)[keyof typeof PorteeParametre];

/** État de validation d'une valeur usine (25 paramètres sur 45 sont à confirmer). */
export const StatutValidationParametre = {
  VALIDE: 'VALIDE',
  A_CONFIRMER: 'A_CONFIRMER',
  MANQUANT: 'MANQUANT',
} as const;
export type StatutValidationParametre =
  (typeof StatutValidationParametre)[keyof typeof StatutValidationParametre];

/** Type d'une trame montante déposée par l'objet. */
export const TypeMessageMontant = {
  HEARTBEAT: 'HEARTBEAT',
  MEASURE: 'MEASURE',
  POSITION: 'POSITION',
  EVENT: 'EVENT',
  ACK: 'ACK',
  BOOT: 'BOOT',
  DIAG: 'DIAG',
  STATE: 'STATE',
} as const;
export type TypeMessageMontant =
  (typeof TypeMessageMontant)[keyof typeof TypeMessageMontant];

/** Évènements discrets remontés par l'objet dans une trame EVENT. */
export const TypeEvenementObjet = {
  POWER_ON: 'POWER_ON',
  POWER_OFF: 'POWER_OFF',
  MOVEMENT_START: 'MOVEMENT_START',
  MOVEMENT_END: 'MOVEMENT_END',
  LOW_BATTERY: 'LOW_BATTERY',
  BACKUP_LOW_BATTERY: 'BACKUP_LOW_BATTERY',
  TAMPER: 'TAMPER',
} as const;
export type TypeEvenementObjet =
  (typeof TypeEvenementObjet)[keyof typeof TypeEvenementObjet];

/** Acteur d'une entrée du journal d'audit. */
export const ActeurAudit = {
  UTILISATEUR: 'UTILISATEUR',
  OBJET: 'OBJET',
  BACKOFFICE: 'BACKOFFICE',
  SYSTEME: 'SYSTEME',
} as const;
export type ActeurAudit = (typeof ActeurAudit)[keyof typeof ActeurAudit];

/* -------------------------------------------------------------------------- */
/* Valeurs autorisées figées par la spec                                       */
/* -------------------------------------------------------------------------- */

/** Fréquences de remontée GPS autorisées, en minutes (arbitrage {5,15,30}). */
export const FREQUENCES_GEOLOC_MIN = [5, 15, 30] as const;
export type FrequenceGeolocMin = (typeof FREQUENCES_GEOLOC_MIN)[number];

/** Fenêtres d'historique disponibles, dans l'ordre d'affichage de l'écran 04E. */
export const PERIODES_HISTORIQUE = [
  PeriodeHistorique.H2,
  PeriodeHistorique.H24,
  PeriodeHistorique.J7,
] as const;
